import React, { FC, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { Translate } from "@/app/application/Translate";
import { SessionType } from "@/features/training/model/Training/TrainingDrill";
import { NewDrillSession } from "@/features/training/pages/drill-tab/panel-right/drill/NewDrillSession";
import { CustomActionsButton } from "@/features/training/ui";
import { EditTrainingDrill } from "@/features/training/ui";
import { StyledMenu } from "@/shared/ui/StyledMenu";
import EngineeringIcon from "@mui/icons-material/Engineering";
import FestivalIcon from "@mui/icons-material/Festival";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import InventoryIcon from "@mui/icons-material/Inventory";
import {
	Box,
	Divider,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Tooltip,
} from "@mui/material";

interface TrainingDrillActionsProps {
	drillId: number;
	type: SessionType;
}

export const TrainingDrillActions: FC<TrainingDrillActionsProps> = ({
	drillId,
	type,
}) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const showArchivedSessions = pathname.includes("ArchivedSessions");

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [editDrillModal, setEditDrillModal] = useState(false);
	const [newSessionModal, setNewSessionModal] = useState(false);

	const open = Boolean(anchorEl);
	const handleClick = (e: React.MouseEvent<HTMLElement>) =>
		setAnchorEl(e.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const openEdit = () => {
		setEditDrillModal(true);
		handleClose();
	};
	const openNewSession = () => {
		setNewSessionModal(true);
		handleClose();
	};
	const toggleSessionsType = () => {
		const path = showArchivedSessions
			? `/training/drills/${drillId}`
			: `/training/archive`;
		navigate(path);
		handleClose();
	};

	const isExercise = type === SessionType.Exercise;
	const EditIcon = isExercise ? EngineeringIcon : FestivalIcon;

	// Compact menu styling
	const paperSx = useMemo(
		() => ({
			borderRadius: 3,
			mt: 1,
			minWidth: 240,
			overflow: "hidden",
			border: "1px solid",
			borderColor: "divider",
			boxShadow:
				"0px 14px 30px rgba(0,0,0,0.12), 0px 6px 12px rgba(0,0,0,0.08)",
		}),
		[],
	);

	const itemSx = useMemo(
		() => ({
			mx: 0.75,
			my: 0.25,
			borderRadius: 2,
			px: 1,
			py: 0.75,
			minHeight: 38,
			"& .MuiListItemIcon-root": {
				minWidth: 30,
				color: "text.primary",
			},
			"& .MuiListItemText-primary": {
				fontSize: 14.5,
				fontWeight: 500,
				letterSpacing: 0.1,
			},
			"&:hover": { backgroundColor: "action.hover" },
		}),
		[],
	);

	return (
		<Box>
			<Tooltip
				title={
					<Translate String="Training.Drill.Actions.Tooltip">
						Edit this drill, create a session, or switch views
					</Translate>
				}
				enterDelay={600}
			>
				<Box display="inline-flex">
					<CustomActionsButton
						open={open}
						handleClick={handleClick}
						aria-haspopup="menu"
						aria-controls={open ? "training-drill-actions-menu" : undefined}
						aria-expanded={open ? "true" : undefined}
					>
						<Translate String="Training.Actions">Actions</Translate>
					</CustomActionsButton>
				</Box>
			</Tooltip>

			<StyledMenu
				id="training-drill-actions-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				keepMounted
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ sx: paperSx }}
				MenuListProps={{
					"aria-labelledby": "training-drill-actions-button",
					dense: false,
					sx: { py: 0.5 },
				}}
			>
				<MenuItem sx={itemSx} onClick={openEdit}>
					<ListItemIcon>
						<EditIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							isExercise ? (
								<Translate
									HTMLTag="span"
									String="Training.Drill.Actions.EditExercise"
								>
									Edit exercise
								</Translate>
							) : (
								<Translate
									HTMLTag="span"
									String="Training.Drill.Actions.EditEvent"
								>
									Edit event
								</Translate>
							)
						}
					/>
				</MenuItem>

				<MenuItem sx={itemSx} onClick={openNewSession}>
					<ListItemIcon>
						<HistoryEduIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							<Translate
								HTMLTag="span"
								String="Training.Drill.Actions.NewExercise"
							>
								New session
							</Translate>
						}
					/>
				</MenuItem>

				<Divider sx={{ my: 0.5, mx: 1.5 }} />

				<MenuItem sx={itemSx} onClick={toggleSessionsType}>
					<ListItemIcon>
						<InventoryIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText
						primary={
							showArchivedSessions ? (
								<Translate
									HTMLTag="span"
									String="Training.Drill.Actions.ActiveSessions"
								>
									Active sessions
								</Translate>
							) : (
								<Translate
									HTMLTag="span"
									String="Training.Drill.Actions.ArchivedSessions"
								>
									Archived sessions
								</Translate>
							)
						}
					/>
				</MenuItem>
			</StyledMenu>

			{/* Modals */}
			<EditTrainingDrill
				id={drillId}
				type={type}
				open={editDrillModal}
				handleClose={() => setEditDrillModal(false)}
			/>
			<NewDrillSession
				drillId={drillId}
				open={newSessionModal}
				handleClose={() => setNewSessionModal(false)}
			/>
		</Box>
	);
};
